import { motion } from "framer-motion";
import { useLanguage } from "../i18n/LanguageContext";
import { Reveal, SectionLabel } from "./Reveal";

const MAX = 431;

const RegionBar = ({ name, value, index }) => (
  <div data-testid={`geo-region-${index + 1}`} className="grid grid-cols-12 gap-3 md:gap-6 items-baseline py-3 md:py-4">
    <span className="col-span-5 md:col-span-3 text-sm md:text-base text-foreground/75 tracking-wide">{name}</span>
    <div className="col-span-5 md:col-span-7 h-px bg-foreground/10 relative self-center">
      <motion.div
        className="absolute left-0 top-0 h-px bg-foreground"
        initial={{ width: 0 }}
        whileInView={{ width: `${(value / MAX) * 100}%` }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 1.2, delay: 0.1 + index * 0.08, ease: [0.16, 1, 0.3, 1] }}
      />
    </div>
    <span className="col-span-2 text-right font-mono text-xs md:text-sm text-foreground/50">{value}</span>
  </div>
);

export const Network = () => {
  const { t } = useLanguage();
  const n = t.network || {};
  const stats = Array.isArray(n.stats) ? n.stats : [];
  const regions = Array.isArray(n.regions) ? n.regions : [];

  return (
    <section id="network" data-testid="network-section" className="py-24 md:py-60 bg-muted/40">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <div className="grid grid-cols-12 gap-4 md:gap-16">
          <div className="col-span-12 md:col-span-5 md:col-start-2">
            <Reveal>
              <SectionLabel>{n.label}</SectionLabel>
            </Reveal>
            <Reveal delay={0.1}>
              <h2
                data-testid="network-heading"
                className="mt-10 font-normal text-3xl md:text-4xl tracking-tight leading-[1.15]"
              >
                {n.heading}
              </h2>
            </Reveal>
            <Reveal delay={0.2} className="mt-12 md:mt-14 max-w-lg space-y-8">
              <p className="text-base md:text-lg leading-relaxed text-foreground/75 tracking-wide">{n.intro}</p>
              <p className="text-sm md:text-base leading-relaxed text-foreground/60 tracking-wide italic">
                {n.closing}
              </p>
            </Reveal>
          </div>

          <div className="col-span-12 md:col-span-4 md:col-start-8 md:pt-24">
            {stats.map((s, i) => (
              <Reveal key={i} delay={0.05 * i}>
                <div
                  data-testid={`network-stat-${i + 1}`}
                  className="border-t border-foreground/15 last:border-b py-5 md:py-6 flex items-baseline justify-between gap-6"
                >
                  <span className="font-normal text-2xl md:text-3xl tracking-tight">{s.value}</span>
                  <span className="text-sm text-foreground/60 tracking-wide text-right">{s.label}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="mt-24 md:mt-36 grid grid-cols-12">
          <div className="col-span-12 md:col-span-8 md:col-start-3">            <Reveal>
              <p className="text-xs uppercase tracking-[0.25em] text-foreground/50">{n.geoLabel}</p>
              <p data-testid="geo-intro" className="mt-4 text-base md:text-lg text-foreground/75 tracking-wide">
                {n.geoIntro}
              </p>
            </Reveal>
            <div className="mt-10 md:mt-12 border-t border-foreground/15 pt-2">
              {regions.map((r, i) => (
                <RegionBar key={r.name} name={r.name} value={r.value} index={i} />
              ))}
            </div>
            <Reveal delay={0.2}>
              <p data-testid="geo-countries" className="mt-10 text-xs md:text-sm leading-loose text-foreground/50 tracking-wide">
                {n.countries}
              </p>
              <p className="mt-2 text-xs md:text-sm text-foreground/50 tracking-wide">{n.countriesNote}</p>
              <p data-testid="geo-note" className="mt-8 text-[11px] text-foreground/40 tracking-wide">
                {n.geoNote}
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
